import {
  COLD_CALL_MODULE_ID,
  DISCOVERY_MODULE_ID,
  OBJECTION_MODULE_ID,
  ON_THE_FLY_MODULE_ID,
  PITCH_MODULE_ID,
  PLAY_MODULE_ID,
} from "@/lib/on-the-fly";
import { MANAGEMENT_MODULE_ID } from "@/lib/rubrics/constants";
import {
  AI_TEST_CONTENT_TYPE_ORDER,
  AI_TEST_CONTENT_TYPES,
} from "@/lib/ai-content-builder/test-content-types";
import { MAX_GENERATION_COUNT } from "@/lib/ai-content-builder/generation-limits";

export { MAX_GENERATION_COUNT } from "@/lib/ai-content-builder/generation-limits";

function textField(name, label, options = {}) {
  return { name, label, type: "text", ...options };
}

function textareaField(name, label, options = {}) {
  return { name, label, type: "textarea", rows: 4, ...options };
}

function listField(name, label, options = {}) {
  return { name, label, type: "list", rows: 4, ...options };
}

function selectField(name, label, options = {}) {
  return { name, label, type: "select", options: [], ...options };
}

function booleanField(name, label, options = {}) {
  return { name, label, type: "boolean", ...options };
}

// Structured field with its own editor (CandidateFields dispatches on `type`).
// A rubric's substance is its criteria; a flat text input cannot represent the
// grouped criterion → performance-level tree they arrive in.
function rubricCriteriaField(name, label, options = {}) {
  return { name, label, type: "rubric-criteria", ...options };
}

function normalizeString(value) {
  return typeof value === "string" ? value.trim() : "";
}

function firstNormalizedString(...values) {
  return values.map(normalizeString).find(Boolean) || "";
}

function normalizeStringArray(values = [], maxItems = 8, maxLength = 500) {
  if (!Array.isArray(values)) return [];
  return values
    .map((value) => normalizeString(value).slice(0, maxLength))
    .filter(Boolean)
    .slice(0, maxItems);
}

// Display order for the in-app builder UI. Types registered in
// AI_CONTENT_TYPES but NOT listed here (e.g. management-scenarios) are still
// resolvable for the MCP tool layer — list/create work everywhere; only the
// builder's pickers hide them.
export const AI_CONTENT_TYPE_ORDER = [
  "personas",
  "faqs",
  "glossary",
  "fbiv",
  "rubrics",
  ...AI_TEST_CONTENT_TYPE_ORDER,
  "objection-scenarios",
  "pitch-scenarios",
  "discovery-scenarios",
  "discovery-trees",
  "outbound-scenarios",
  "play-scenarios",
];

export const SHARED_COUNT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];
const FAQ_COUNT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 10, 15, 20];

export const AI_CONTENT_TYPES = {
  personas: {
    id: "personas",
    label: "Personas",
    singularLabel: "persona",
    description: "Create buyer personas for your studio.",
    domain: "setup",
    generateTool: "generatePersonaCandidates",
    saveTool: "saveGeneratedPersonas",
    countOptions: SHARED_COUNT_OPTIONS,
    defaultCount: 3,
    countParam: "requestedCount",
    generatedKeys: ["personas"],
    savedKeys: ["createdPersonas"],
    adminPath: "/workspaces/admin/data/personas",
    buildSaveParams: ({ candidates, selectedIds, workspaceId }) => ({
      workspaceId,
      generatedPersonas: candidates,
      selectedPersonaIds: selectedIds,
      assignAvatarByGender: true,
      createPersonaOptions: {
        voice: "nova",
        labels: [],
      },
    }),
    getItemTitle: (item, index = 0) =>
      normalizeString(item?.name) || `Persona ${index + 1}`,
    fields: [
      textField("name", "Name"),
      textField("title", "Title"),
      textField("role", "Role"),
      textareaField("description", "Description"),
      textareaField("backStory", "Backstory"),
      listField("challenges", "Challenges", {
        helpText: "One challenge per line.",
      }),
      listField("goals", "Goals", {
        helpText: "One goal per line.",
      }),
      textareaField("decisionProcess", "Decision process"),
      textareaField("hiddenConcerns", "Hidden concerns"),
      textareaField("sourcesOfInfo", "Sources of info"),
      listField("keyMessaging", "Key messaging", {
        helpText: "One message per line.",
      }),
      listField("discoveryQuestions", "Discovery questions", {
        helpText: "One question per line.",
      }),
    ],
  },
  faqs: {
    id: "faqs",
    label: "Q&As",
    singularLabel: "Q&A",
    description: "Create common questions and answers for your studio.",
    domain: "setup",
    generateTool: "generateFAQCandidates",
    saveTool: "saveGeneratedFAQs",
    countOptions: FAQ_COUNT_OPTIONS,
    defaultCount: 5,
    countParam: "count",
    generatedKeys: ["faqs"],
    savedKeys: ["createdFAQs"],
    adminPath: "/workspaces/admin/faqs",
    buildSaveParams: ({ candidates, selectedIds, workspaceId, saveOptions }) => ({
      workspaceId,
      generatedFAQs: candidates,
      selectedFAQIds: selectedIds,
      personaSelections:
        saveOptions?.personaSelections &&
        typeof saveOptions.personaSelections === "object"
          ? saveOptions.personaSelections
          : {},
      // Batch group assignment picked in the review step — every saved FAQ
      // lands in these groups at creation time.
      groupIds: normalizeStringArray(saveOptions?.groupIds, 50, 100),
    }),
    getItemTitle: (item, index = 0) =>
      normalizeString(item?.question) || `Q&A ${index + 1}`,
    fields: [
      textField("question", "Question"),
      textareaField("answer", "Answer", { rows: 6 }),
      textField("category", "Category", { optional: true }),
    ],
  },
  glossary: {
    id: "glossary",
    label: "Glossary entries",
    singularLabel: "glossary entry",
    description: "Create terms your team should use consistently.",
    domain: "setup",
    generateTool: "generateGlossaryCandidates",
    saveTool: "saveGeneratedGlossaryEntries",
    countOptions: SHARED_COUNT_OPTIONS,
    defaultCount: 5,
    countParam: "count",
    generatedKeys: ["entries"],
    savedKeys: ["createdEntries"],
    adminPath: "/workspaces/admin/glossary",
    buildSaveParams: ({ candidates, selectedIds, workspaceId }) => ({
      workspaceId,
      generatedEntries: candidates,
      selectedEntryIds: selectedIds,
      usedForFeedback: true,
    }),
    getItemTitle: (item, index = 0) =>
      normalizeString(item?.term) || `Glossary entry ${index + 1}`,
    fields: [
      textField("term", "Term"),
      textareaField("definition", "What it is"),
      textareaField("usage", "Why we use it"),
      textareaField("timing", "When we use it"),
    ],
  },
  fbiv: {
    id: "fbiv",
    label: "Capability map entries",
    singularLabel: "capability map entry",
    description: "Create capability maps from feature, benefit, impact, and value entries.",
    domain: "setup",
    generateTool: "generateFBIVCandidates",
    saveTool: "saveGeneratedFBIVEntries",
    countOptions: SHARED_COUNT_OPTIONS,
    defaultCount: 5,
    countParam: "count",
    generatedKeys: ["fbivs", "examples"],
    savedKeys: ["createdEntries", "createdFBIVs"],
    adminPath: "/workspaces/admin/fbiv",
    buildSaveParams: ({ candidates, selectedIds, workspaceId }) => ({
      workspaceId,
      generatedFBIVs: candidates,
      selectedFBIVIds: selectedIds,
    }),
    getItemTitle: (item, index = 0) =>
      normalizeString(item?.feature) || `Capability map entry ${index + 1}`,
    fields: [
      textField("feature", "Feature"),
      textareaField("benefit", "Capability"),
      textareaField("impact", "Benefit / Impact"),
      textField("valueTheme", "Value theme"),
      textareaField("soundbite", "Soundbite"),
    ],
  },
  rubrics: {
    id: "rubrics",
    label: "Rubrics",
    singularLabel: "rubric",
    description: "Create evaluation rubrics for your studio.",
    domain: "setup",
    generateTool: "generateRubricCandidates",
    saveTool: "saveGeneratedRubrics",
    // A rubric scores practice inside exactly one module, and nothing in the
    // source material implies which one. The builder asks up front (module
    // chips in BuilderConfigureStep) rather than letting the generator fail
    // with "Please specify which module the rubric should belong to".
    requiresModule: true,
    // Most studio rubrics score plays, so that module starts selected. A
    // studio without it still has to pick one deliberately — better than
    // filing a rubric under whichever module happens to sort first.
    defaultModuleId: PLAY_MODULE_ID,
    countOptions: [1],
    defaultCount: 1,
    countParam: "count",
    generatedKeys: ["rubrics", "items"],
    savedKeys: ["createdRubrics", "items"],
    adminPath: "/workspaces/admin/data/rubrics",
    buildSaveParams: ({
      candidates,
      selectedIds,
      selectedItems,
      workspaceId,
      moduleId,
      moduleName,
      scale,
      rubricName,
      narrativeDescription,
    }) => ({
      workspaceId,
      generatedRubrics: candidates,
      selectedRubricIds: selectedIds,
      selectedRubrics: selectedItems,
      moduleId,
      moduleName,
      scale,
      rubricName,
      narrativeDescription,
    }),
    getItemTitle: (item, index = 0) =>
      normalizeString(item?.name) || `Rubric ${index + 1}`,
    fields: [
      textField("name", "Name"),
      textField("moduleName", "Module"),
      rubricCriteriaField("criteria", "Criteria"),
      textareaField("narrativeDescription", "Narrative description", {
        optional: true,
      }),
      {
        name: "evaluationGuidance",
        label: "Evaluation guidance (optional)",
        type: "rubric-guidance",
        optional: true,
      },
    ],
  },
  "objection-scenarios": {
    id: "objection-scenarios",
    label: "Objection scenarios",
    singularLabel: "objection scenario",
    description: "Create objection scenarios for practice.",
    domain: "scenario",
    generateTool: "generateObjectionCandidates",
    saveTool: "saveGeneratedObjections",
    countOptions: SHARED_COUNT_OPTIONS,
    defaultCount: 3,
    countParam: "count",
    generatedKeys: ["examples"],
    savedKeys: ["items", "createdObjections", "examples"],
    adminPath: "/workspaces/admin/data/objections",
    rubricModuleIds: [OBJECTION_MODULE_ID],
    buildSaveParams: ({ candidates, selectedIds, workspaceId, rubricId }) => ({
      workspaceId,
      generatedObjections: candidates,
      selectedObjectionIds: selectedIds,
      rubricId,
    }),
    getItemTitle: (item, index = 0) =>
      normalizeString(item?.objection) || `Objection scenario ${index + 1}`,
    fields: [
      textField("objection", "Objection"),
      listField("response", "Response strategies", {
        helpText: "One response per line.",
      }),
      textareaField("realConcern", "Real concern"),
      textareaField("keyMessage", "Key message"),
      textareaField("opening", "Opening"),
      textareaField("onboardingAccommodation", "Onboarding accommodation", {
        optional: true,
      }),
      selectField("stage", "Stage", {
        options: [
          { label: "Early", value: "EARLY" },
          { label: "Late", value: "LATE" },
        ],
      }),
    ],
  },
  "pitch-scenarios": {
    id: "pitch-scenarios",
    label: "Pitch scenarios",
    singularLabel: "pitch scenario",
    description: "Create pitch scenarios for practice.",
    domain: "scenario",
    generateTool: "generatePitchExampleCandidates",
    createTool: "createPitchSimulationScenario",
    saveMode: "create",
    countOptions: SHARED_COUNT_OPTIONS,
    defaultCount: 3,
    countParam: "count",
    generatedKeys: ["examples"],
    adminPath: "/workspaces/admin/data/pitch",
    rubricModuleIds: [PITCH_MODULE_ID],
    getItemTitle: (item, index = 0) =>
      firstNormalizedString(item?.title, item?.pitch, item?.name) ||
      `Pitch scenario ${index + 1}`,
    buildCreateParams: (item = {}) => ({
      title: firstNormalizedString(item?.title, item?.pitch, item?.name),
      pitch: firstNormalizedString(item?.title, item?.pitch, item?.name),
      overview:
        normalizeString(item?.overview) ||
        normalizeString(item?.scenario) ||
        normalizeString(item?.focus) ||
        normalizeString(item?.goal),
      content: normalizeString(item?.content),
      examples: normalizeStringArray(
        item?.examples?.length ? item.examples : item?.keyPoints
      ),
      customerOpening: normalizeString(item?.customerOpening),
      focus: normalizeString(item?.focus),
      goal: normalizeString(item?.goal),
      type: normalizeString(item?.type),
      situationBackground:
        normalizeString(item?.situationBackground) ||
        normalizeString(item?.scenario),
      onboardingAccommodation: normalizeString(item?.onboardingAccommodation),
      passiveMode: Boolean(item?.passiveMode),
      questionMode: Boolean(item?.questionMode),
      glossaryGroupId: item?.glossaryGroupId || null,
      requiredQuestionsBot: normalizeStringArray(item?.requiredQuestionsBot),
    }),
    fields: [
      textField("title", "Title"),
      textareaField("scenario", "Overview", {
        helpText: "How this pitch should be positioned.",
      }),
      textareaField("content", "Pitch content", { rows: 8 }),
      listField("keyPoints", "Key points", {
        helpText: "One key point per line.",
      }),
      textareaField("customerOpening", "Customer opening", { optional: true }),
      textareaField("focus", "Focus", { optional: true }),
      textareaField("goal", "Goal", { optional: true }),
      textField("type", "Type", { optional: true }),
    ],
  },
  "discovery-scenarios": {
    id: "discovery-scenarios",
    label: "Discovery scenarios",
    singularLabel: "discovery scenario",
    description: "Create discovery scenarios for practice.",
    domain: "scenario",
    generateTool: "generateDiscoveryQuestionCandidates",
    createTool: "createDiscoverySimulationScenario",
    saveMode: "create",
    countOptions: SHARED_COUNT_OPTIONS,
    defaultCount: 3,
    countParam: "count",
    generatedKeys: ["questions"],
    adminPath: "/workspaces/admin/data/discovery",
    rubricModuleIds: [DISCOVERY_MODULE_ID],
    getItemTitle: (item, index = 0) =>
      normalizeString(item?.title || item?.trackName) ||
      `Discovery scenario ${index + 1}`,
    buildCreateParams: (item = {}) => {
      const questions = normalizeStringArray(item?.questions, 12, 500);
      const requiredQuestions = normalizeStringArray(
        item?.requiredQuestions?.length > 0
          ? item.requiredQuestions
          : questions.slice(0, Math.min(questions.length, 3)),
        8,
        500
      );

      return {
        title: normalizeString(item?.title || item?.trackName),
        trackName: normalizeString(item?.title || item?.trackName),
        questions,
        requiredQuestions,
        opening: normalizeString(item?.opening),
        customerOpening: normalizeString(item?.customerOpening),
        goal:
          normalizeString(item?.goal) ||
          normalizeStringArray(item?.keyInsights, 3, 240).join(" "),
        situationBackground:
          normalizeString(item?.situationBackground) ||
          normalizeStringArray(item?.keyInsights, 4, 240).join("\n"),
        onboardingAccommodation: normalizeString(item?.onboardingAccommodation),
        passiveMode: Boolean(item?.passiveMode),
        questionMode: Boolean(item?.questionMode),
        glossaryGroupId: item?.glossaryGroupId || null,
        requiredQuestionsBot: normalizeStringArray(item?.requiredQuestionsBot),
      };
    },
    fields: [
      textField("title", "Title"),
      listField("questions", "Questions", {
        rows: 6,
        helpText: "One question per line.",
      }),
      listField("keyInsights", "Key insights", {
        helpText: "Optional coaching notes.",
      }),
      listField("followUpTips", "Follow-up tips", {
        helpText: "Optional next-step prompts.",
      }),
      textareaField("goal", "Goal", { optional: true }),
      textareaField("opening", "Opening", { optional: true }),
      textareaField("customerOpening", "Customer opening", {
        optional: true,
      }),
    ],
  },
  // Discovery trees are playbook question sets (Example records), not practice
  // scenarios — same generator as discovery-scenarios, different save target.
  // Previously they could only be generated from a simulation result (see
  // AIDiscoveryQuestionFromResultDialog); this type lets admins build them from
  // studio context, a document, a resource, or a URL.
  "discovery-trees": {
    id: "discovery-trees",
    label: "Discovery trees",
    singularLabel: "discovery tree",
    description: "Create discovery question trees for your team's playbook.",
    domain: "scenario",
    generateTool: "generateDiscoveryQuestionCandidates",
    saveTool: "saveGeneratedDiscoveryQuestions",
    countOptions: SHARED_COUNT_OPTIONS,
    defaultCount: 3,
    countParam: "count",
    generatedKeys: ["questions"],
    savedKeys: ["items", "questions"],
    adminPath: "/playbook",
    rubricModuleIds: [DISCOVERY_MODULE_ID],
    buildSaveParams: ({ candidates, selectedIds, workspaceId, rubricId }) => ({
      workspaceId,
      generatedQuestions: candidates,
      selectedQuestionIds: selectedIds,
      rubricId,
    }),
    getItemTitle: (item, index = 0) =>
      normalizeString(item?.title) || `Discovery tree ${index + 1}`,
    fields: [
      textField("title", "Title"),
      selectField("category", "Category", {
        options: [
          { label: "Situation", value: "situation" },
          { label: "Problem", value: "problem" },
          { label: "Implication", value: "implication" },
          { label: "Need-payoff", value: "need-payoff" },
        ],
      }),
      listField("questions", "Questions", {
        rows: 6,
        helpText: "One question per line.",
      }),
      listField("keyInsights", "Key insights", {
        helpText: "What these questions should uncover — one per line.",
      }),
      listField("followUpTips", "Follow-up tips", {
        helpText: "How to use this track — one per line.",
      }),
    ],
  },
  "outbound-scenarios": {
    id: "outbound-scenarios",
    label: "Outbound scenarios",
    singularLabel: "outbound scenario",
    description: "Create outbound scenarios for practice.",
    domain: "scenario",
    generateTool: "generateOutboundExampleCandidates",
    createTool: "createOutboundSimulationScenario",
    saveMode: "create",
    countOptions: SHARED_COUNT_OPTIONS,
    defaultCount: 3,
    countParam: "count",
    generatedKeys: ["examples"],
    adminPath: "/workspaces/admin/data/cold-call",
    rubricModuleIds: [COLD_CALL_MODULE_ID, PITCH_MODULE_ID],
    getItemTitle: (item, index = 0) =>
      normalizeString(item?.title || item?.name) ||
      `Outbound scenario ${index + 1}`,
    buildCreateParams: (item = {}) => ({
      title: normalizeString(item?.title || item?.name),
      name: normalizeString(item?.title || item?.name),
      script: normalizeString(item?.script || item?.content),
      content: normalizeString(item?.script || item?.content),
      situationBackground:
        normalizeString(item?.situationBackground) ||
        normalizeString(item?.scenario),
      customerOpening: normalizeString(item?.customerOpening),
      examples: normalizeStringArray(
        item?.examples?.length ? item.examples : item?.keyPoints
      ),
      onboardingAccommodation: normalizeString(item?.onboardingAccommodation),
      passiveMode: Boolean(item?.passiveMode),
      questionMode: Boolean(item?.questionMode),
      glossaryGroupId: item?.glossaryGroupId || null,
      requiredQuestionsBot: normalizeStringArray(item?.requiredQuestionsBot),
    }),
    fields: [
      textField("title", "Title"),
      textareaField("scenario", "Situation background", {
        helpText: "Where this outbound scenario fits best.",
      }),
      textareaField("content", "Script", { rows: 8 }),
      listField("keyPoints", "Examples", {
        helpText: "One example or coaching point per line.",
      }),
      textareaField("customerOpening", "Customer opening", { optional: true }),
    ],
  },
  // Kept out of AI_CONTENT_TYPE_ORDER for now so the in-app picker remains
  // unchanged, but focused learning-path agents can generate these candidates.
  "management-scenarios": {
    id: "management-scenarios",
    label: "Management scenarios",
    singularLabel: "management scenario",
    description: "Create manager-to-rep coaching conversation scenarios.",
    domain: "scenario",
    generateTool: "generateManagementScenarioCandidates",
    createTool: "createManagementSimulationScenario",
    saveMode: "create",
    countOptions: [1, 2, 3],
    defaultCount: 1,
    countParam: "count",
    generatedKeys: ["scenarios"],
    adminPath: "/workspaces/admin/data/management-scenarios",
    rubricModuleIds: [MANAGEMENT_MODULE_ID],
    getItemTitle: (item, index = 0) =>
      normalizeString(item?.name || item?.title) ||
      `Management scenario ${index + 1}`,
    buildCreateParams: (item = {}) => ({
      name: normalizeString(item?.name || item?.title),
      title: normalizeString(item?.name || item?.title),
      description: normalizeString(item?.description || item?.overview),
      situationBackground: normalizeString(item?.situationBackground),
      sellerOpening: normalizeString(item?.sellerOpening || item?.customerOpening),
      context: normalizeString(item?.context || item?.otherInformation),
      objectives: normalizeStringArray(item?.objectives, 12, 400),
      commonChallenges: normalizeStringArray(item?.commonChallenges, 12, 400),
    }),
    fields: [
      textField("name", "Name"),
      textareaField("description", "Description"),
      textareaField("situationBackground", "Situation background"),
      textareaField("sellerOpening", "Seller opening", { optional: true }),
      listField("objectives", "Objectives", {
        helpText: "One objective per line.",
      }),
      listField("commonChallenges", "Common challenges", {
        helpText: "One challenge per line.",
      }),
      textareaField("context", "Other context", { optional: true }),
    ],
  },
  "play-scenarios": {
    id: "play-scenarios",
    label: "Play scenarios",
    singularLabel: "play scenario",
    description: "Create play scenarios for practice.",
    domain: "scenario",
    generateTool: "generatePlayScenarioCandidates",
    saveTool: "saveGeneratedPlayScenarios",
    countOptions: SHARED_COUNT_OPTIONS,
    defaultCount: 3,
    countParam: "count",
    generatedKeys: ["scenarios", "content"],
    savedKeys: ["items", "scenarios"],
    adminPath: "/workspaces/admin/data/plays",
    rubricModuleIds: [PLAY_MODULE_ID, ON_THE_FLY_MODULE_ID],
    buildSaveParams: ({ candidates, selectedIds, workspaceId, rubricId }) => ({
      workspaceId,
      generatedScenarios: candidates,
      selectedScenarioIds: selectedIds,
      rubricId,
    }),
    getItemTitle: (item, index = 0) =>
      normalizeString(item?.name || item?.title) ||
      `Play scenario ${index + 1}`,
    fields: [
      textField("name", "Name"),
      selectField("channel", "Channel", {
        options: [
          { label: "Outbound", value: "OUTBOUND" },
          { label: "Inbound", value: "INBOUND" },
        ],
      }),
      selectField("customerStatus", "Customer status", {
        options: [
          { label: "New customer", value: "NEW_CUSTOMER" },
          { label: "Existing customer", value: "EXISTING_CUSTOMER" },
        ],
      }),
      selectField("contactType", "Contact type", {
        options: [
          { label: "First contact", value: "FIRST_CONTACT" },
          { label: "Follow-up contact", value: "FOLLOW_UP_CONTACT" },
        ],
      }),
      textareaField("situationBackground", "Situation background"),
      textareaField("customerOpening", "Customer opening"),
      textareaField("happenedAlready", "What happened already", {
        optional: true,
      }),
      textareaField("previousContactChanges", "Previous contact changes", {
        optional: true,
      }),
      textareaField("anythingNew", "Anything new", { optional: true }),
      listField("assumptions", "Assumptions", {
        helpText: "One assumption per line.",
      }),
      listField("criticalInformation", "Critical information", {
        helpText: "One detail per line.",
      }),
      listField("personasMainConcerns", "Persona concerns", {
        helpText: "One concern per line.",
      }),
      textareaField("goal", "Goal", { optional: true }),
      textareaField("otherInformation", "Other information", {
        optional: true,
      }),
      booleanField("questionMode", "Question mode"),
    ],
  },
  ...AI_TEST_CONTENT_TYPES,
};

export function getAIContentType(contentType) {
  return AI_CONTENT_TYPES[contentType] || null;
}

// Per-type generation count bounds, so every consumer (in-app builder, agent
// route, MCP tools) clamps identically. countOptions are the quick-pick
// presets; typed/agent-requested counts may go beyond them up to the shared
// MAX_GENERATION_COUNT ceiling. Fixed single-draft types (countOptions [1],
// e.g. rubrics — the generator emits exactly one per run) keep their fixed
// count. Unknown types get the shared ceiling.
export function getContentTypeCountBounds(contentType) {
  const options = AI_CONTENT_TYPES[contentType]?.countOptions;
  if (!Array.isArray(options) || options.length === 0) {
    return { min: 1, max: MAX_GENERATION_COUNT };
  }
  const min = Math.min(...options);
  const optionsMax = Math.max(...options);
  const max =
    optionsMax === 1 ? optionsMax : Math.max(optionsMax, MAX_GENERATION_COUNT);
  return { min, max };
}

// Clamp a requested generation count to the type's supported bounds so every
// consumer (the builder's typed count input, the generation server action,
// agent tools) resolves free-form input the same way. Non-numeric input falls
// back to the type's defaultCount; the fallback is clamped too.
export function clampContentTypeCount(contentType, value, fallback = null) {
  const { min, max } = getContentTypeCountBounds(contentType);
  const parsed = parseInt(value, 10);
  const resolved = Number.isFinite(parsed)
    ? parsed
    : parseInt(fallback ?? AI_CONTENT_TYPES[contentType]?.defaultCount ?? min, 10);
  if (!Number.isFinite(resolved)) {
    return min;
  }
  return Math.min(max, Math.max(min, resolved));
}

export function getAIContentTypeOptions(allowedContentTypes = []) {
  const allowed =
    Array.isArray(allowedContentTypes) && allowedContentTypes.length > 0
      ? new Set(allowedContentTypes)
      : null;

  return AI_CONTENT_TYPE_ORDER.filter((id) =>
    allowed ? allowed.has(id) : true
  ).map((id) => ({
    id,
    label: AI_CONTENT_TYPES[id].label,
  }));
}

export function extractGeneratedCandidates(contentType, data = {}) {
  const config = getAIContentType(contentType);
  if (!config || !data || typeof data !== "object") {
    return [];
  }

  for (const key of config.generatedKeys || []) {
    if (Array.isArray(data[key])) {
      return data[key];
    }
  }

  return [];
}

export function extractSavedItems(contentType, data = {}, fallbackItems = []) {
  const config = getAIContentType(contentType);
  if (!config || !data || typeof data !== "object") {
    return Array.isArray(fallbackItems) ? fallbackItems : [];
  }

  if (Array.isArray(data.items)) {
    return data.items;
  }

  for (const key of config.savedKeys || []) {
    if (Array.isArray(data[key])) {
      return data[key];
    }
  }

  return Array.isArray(fallbackItems) ? fallbackItems : [];
}

export function getContentItemTitle(contentType, item, index = 0) {
  const config = getAIContentType(contentType);
  if (!config) return `Item ${index + 1}`;
  return config.getItemTitle(item, index);
}
